"use client";

import React from "react";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FutureProofYourWorkflow() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="5364" id="final-call-to-action" className="dark bg-background text-foreground relative w-full overflow-hidden py-24 md:py-32">
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,var(--color-primary)_0%,var(--color-background)_70%)] opacity-10" />
      
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div className="animate-scan-line absolute top-0 left-0 h-[2px] w-full bg-primary/40 shadow-[0_0_15px_rgba(var(--color-primary),0.6)]" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div ref={ref} className={`mx-auto max-w-3xl transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="mb-12 text-center">
            <h2 className="text-4xl font-default font-bold tracking-tighter sm:text-5xl md:text-6xl">
              Final Call to Action Lead Form
            </h2>
            <p className="text-muted-foreground mt-4 text-lg md:text-xl font-default">
              <span className="text-primary font-bold mr-2 uppercase tracking-widest">[ Limited Allocation ]</span>
              Join our exclusive circle of investors and lock in institutional-grade returns.
            </p>
          </div>

          <div className="bg-card/40 border-border/40 backdrop-blur-xl rounded-xl border p-8 shadow-2xl md:p-12">
            <FormProvider formKey="final_cta_form_businessoverview" formId="final_cta_form_businessoverview" sectionName="future-proof-workflow" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            investment_budget: "",
            message: "",
            agree_terms: false
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            investment_budget: {
              required: true
            },
            message: {
              required: false
            },
            agree_terms: {
              required: true
            }
          }} submitText="SECURE MY 10% YIELD" successMessage="Thank you for your interest. Your investment pack has been sent to your email, and a senior advisor will contact you within 24 hours." errorMessage="There was an error processing your request. Please check your details and try again.">
              <FutureProofForm />
            </FormProvider>
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes scan-line {
          0% {
            top: 0%;
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            top: 100%;
            opacity: 0;
          }
        }
        .animate-scan-line {
          animation: scan-line 4s linear infinite;
        }
      `}</style>
    </section>;
}
function FutureProofForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="bg-primary/20 mb-6 flex size-20 items-center justify-center rounded-full">
          <CheckCircle2 className="text-primary size-10" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Investment Registered</h3>
        <p className="text-muted-foreground mt-2 max-w-md font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-primary/40 hover:bg-primary/10 hover:text-primary font-default">
          Restart Inquiry
        </Button>
      </div>;
  }
  const minimalistInputClass = "border-0 border-b border-border bg-transparent px-0 py-2 rounded-none shadow-none focus-visible:ring-0 focus-visible:border-primary focus-visible:border-b-2 transition-all duration-300 placeholder:text-muted-foreground/30 h-auto text-lg font-default";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/90 text-xs uppercase tracking-widest font-default font-semibold">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className={minimalistInputClass} />
              </FormControl>
              <FormDescription className="text-[10px] mt-1 font-default italic opacity-60">
                Enter your full name as it appears on official documents.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/90 text-xs uppercase tracking-widest font-default font-semibold">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className={minimalistInputClass} />
              </FormControl>
              <FormDescription className="text-[10px] mt-1 font-default italic opacity-60">
                We'll send the investment pack to this address.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/90 text-xs uppercase tracking-widest font-default font-semibold">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44" {...field} className={minimalistInputClass} />
              </FormControl>
              <FormDescription className="text-[10px] mt-1 font-default italic opacity-60">
                Include country code for best reach.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="investment_budget" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary/90 text-xs uppercase tracking-widest font-default font-semibold">
                Investment Budget
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={`${minimalistInputClass} w-full`}>
                    <SelectValue placeholder="£100,000+" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {["£10,000 - £25,000", "£25,001 - £50,000", "£50,001 - £100,000", "£100,001+"].map(option => <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary/90 text-xs uppercase tracking-widest font-default font-semibold">
              Additional Details (optional)
            </FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Any specific requirements or questions..." className={`${minimalistInputClass} w-full resize-none min-h-24`} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="agree_terms" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-4">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 size-4 rounded border-border bg-background text-primary focus:ring-primary cursor-pointer" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-default cursor-pointer">
                I agree to the Terms & Privacy Policy
              </FormLabel>
              <FormDescription className="text-xs font-default italic opacity-80">
                We respect your privacy and handle data securely.
              </FormDescription>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="bg-destructive/10 border-destructive/20 flex items-center gap-2 rounded-md border p-4 text-destructive">
          <AlertCircle className="size-5 shrink-0" />
          <p className="text-sm font-default">{submitError}</p>
        </div>}

      <div className="pt-6 text-center">
        <Button type="submit" disabled={isSubmitting} size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 h-16 w-full max-w-md rounded-full text-xl font-default font-bold tracking-wide shadow-[0_0_20px_rgba(var(--color-primary),0.4)] transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(var(--color-primary),0.6)]">
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-6 animate-spin" />
              VERIFYING...
            </> : "SECURE MY 10% YIELD"}
        </Button>
      </div>
    </Form>;
}